// The visit report a client opens from a link (/report/<token>).
import { clientLinkTitle, type VisitReportPublic } from "@steward/shared";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import { shortDate } from "../../components/format";
import { Skeleton } from "../../components/parts";
import { Branded, NotFound } from "./common";

/** The client's visit report from its link (draft 7b): what was done, by whom, room by room. */
export function VisitReportPage({ token }: { token: string }) {
  const r = useQuery({
    queryKey: ["public-report", token],
    queryFn: () => api<VisitReportPublic>("GET", `public/report/${token}`),
  });
  if (r.error) return <NotFound what="report" />;
  const d = r.data;
  if (!d)
    return (
      <Branded brand={null}>
        <Skeleton rows={6} />
      </Branded>
    );
  const rooms = [...new Set(d.done.map((x) => x.room ?? ""))];
  return (
    <Branded
      title={clientLinkTitle("report", { brand: d.business }).title}
      brand={{
        name: d.business,
        color: d.brandColor,
        look: d.look,
        phone: null,
        email: null,
        links: [],
        address: null,
        website: null,
      }}
    >
      <h1>
        Hi {d.firstName}, your {d.job.toLowerCase()} is done
      </h1>
      <p className="muted">
        {shortDate(d.date)}
        {d.crew.length ? `, by ${d.crew.join(" and ")}` : ""}.
      </p>
      {d.done.length ? (
        rooms.map((room) => (
          <section key={room} className="stack">
            {room ? <h2>{room}</h2> : null}
            <ul>
              {d.done
                .filter((x) => (x.room ?? "") === room)
                .map((x) => (
                  <li key={x.label}>{x.label}</li>
                ))}
            </ul>
          </section>
        ))
      ) : (
        <p>Everything on your visit is done.</p>
      )}
      {d.note ? <p>{d.note}</p> : null}
    </Branded>
  );
}
